import React, { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GenericTable, If } from 'components';
import { TableColumn } from 'components/generic-table/types';
import { PayRunMemberJob } from '../confirmJob.model';
import { formatAmount, getAmountWithCurrency } from 'utils/amountUtils';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import { CheckboxTickIcon, HyphenIcon, WarningIcon } from 'assets/icons';
import DateUtils from 'utils/dateUtils';
import JobReviewActions from './JobReviewActions';
import { getInvoiceStatus, getInvoiceStatusColor } from 'containers/invoices/utils';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import ExplainCommissionPanel from './ExplainCommissionPanel';
import TagPill from 'containers/tags/create-tag/Tag';
import { getSortedJobTags } from 'containers/jobs/utils';
import ExplainAdjustmentsPanel from './ExplainAdjustmentsPanel';
import { ColumnInfo } from '../../pay-period-review/payperiod.model';
import { selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { getCommissionBaseLabel } from 'containers/settings/utils';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { getEstimateStatusColor } from 'containers/estimates/utils';
import { EstimateStatus } from 'containers/estimates/constants';

interface JobReviewTableProps {
  jobs: PayRunMemberJob[];
  selectedColumns?: string[];
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  selectedJobs?: PayRunMemberJob[];
  onJobCheckboxSelect?: (job: PayRunMemberJob) => void;
  onSelectAll?: () => void;
  fetchData: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  columnInfo?: ColumnInfo[];
  stickyHorizontalScrollbar?: { bottomOffsetPx: number };
  highlightedRowId?: string | null;
}

const JobReviewTable: FC<JobReviewTableProps> = ({
  jobs,
  page,
  totalCount,
  onPageChange,
  selectedJobs = [],
  onJobCheckboxSelect,
  onSelectAll,
  fetchData,
  onSortChange,
  currentSortBy = 'scheduledDate',
  currentSortOrder = 'DESC',
  columnInfo,
  stickyHorizontalScrollbar,
  highlightedRowId
}) => {
  const navigate = useNavigate();
  const { payRunId, merchantId } = useParams();
  const { managePayAdjustments } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();

  const [explainPanelData, setExplainPanelData] = useState<{
    isOpen: boolean;
    jobData?: PayRunMemberJob;
    type: 'commission' | 'adjustment';
  }>({
    isOpen: false,
    jobData: undefined,
    type: 'commission'
  });

  const handleNavigateToJobReview = (jobId: string, status: string, e: React.MouseEvent) => {
    if (!isActiveFSMPlusPlan) {
      return;
    }
    e.stopPropagation(); // Prevent row click handler from firing
    navigate(`/accounting/pay-runs/${payRunId}/members/${merchantId}/jobs/${jobId}`, {
      state: { jobReviewStatus: status }
    });
  };

  const genericColumnInfo = useMemo(
    () =>
      columnInfo?.map(col => ({
        columnKey: col.columnKey,
        isEnabled: col.isEnabled,
        priority: col.priority
      })),
    [columnInfo]
  );

  const columns: TableColumn<PayRunMemberJob>[] = useMemo(
    () => [
      {
        id: 'checkbox',
        header: ({ selectedItems, allItems, onSelectAll: onSelectAllClick }) => {
          const isAllSelected = (allItems?.length ?? 0) > 0 && (selectedItems?.length ?? 0) === allItems?.length;
          const isSomeSelected = (selectedItems?.length ?? 0) > 0;
          let headerIcon: React.ReactNode = null;
          if (isAllSelected) headerIcon = <CheckboxTickIcon className="h-3 w-3 text-white" />;
          else if (isSomeSelected) headerIcon = <HyphenIcon className="h-3 w-3 text-white" />;
          return (
            <div className="flex cursor-pointer items-center gap-6 pl-[14px]">
              <CustomCheckbox
                id="select-all-jobs"
                className={isSomeSelected || isAllSelected ? '' : 'bg-white'}
                isChecked={isSomeSelected || isAllSelected}
                onClick={() => onSelectAllClick?.()}>
                {headerIcon}
              </CustomCheckbox>
            </div>
          );
        },
        accessor: data => (
          <div
            className="flex cursor-pointer items-center gap-6 pl-[14px]"
            onClick={e => {
              e.stopPropagation();
              data.onItemSelect?.(data);
            }}>
            <CustomCheckbox
              className="shrink-0"
              id={`select-job-${data.jobId}`}
              isChecked={data.selectedItems?.some((job: PayRunMemberJob) => job.jobId === data.jobId)}
              onClick={() => data.onItemSelect?.(data)}>
              {data.selectedItems?.some((job: PayRunMemberJob) => job.jobId === data.jobId) && (
                <CheckboxTickIcon className="h-3 w-3 text-white" />
              )}
            </CustomCheckbox>
          </div>
        ),
        fixed: 'left',
        width: 'w-[48px]'
      },
      {
        id: 'jobNumber',
        header: 'Job ID',
        accessor: data => (
          <div className="flex cursor-pointer items-center">
            <span className="whitespace-nowrap font-bold">{data.jobNumber.replaceAll('JOB-', '')}</span>
            <If condition={managePayAdjustments && data.hasDraftAdjustment}>
              <WarningIcon className="path-stroke-current ml-[10px] h-4 w-4 text-pending-orange" />
            </If>
          </div>
        ),
        fixed: 'left',
        width: 'w-[120px]'
      },
      {
        id: 'customerName',
        header: 'Customer',
        accessor: 'customerName',
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'scheduledDate',
        header: 'Date',
        accessor: data =>
          data.scheduledDate
            ? DateUtils.getDateInFormat({ date: new Date(data.scheduledDate), dateFormat: 'dd MMM yyyy' })
            : '-',
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'businessCost',
        header: 'Business costs',
        tooltip: 'Unit costs + Company expenses',
        accessor: data => (data.businessCost ? getAmountWithCurrency(data.businessCost) : '-'),
        width: 'w-[170px]'
      },
      {
        id: 'commissionAmount',
        header: 'Commission',
        accessor: data =>
          data.commissionAmount ? (
            <span className="flex flex-col items-start gap-2">
              {getAmountWithCurrency(data.commissionAmount)}
              <If condition={Boolean(Number(data.commissionAmount))}>
                <button
                  className="cursor-pointer text-secondary"
                  onClick={e => {
                    e.stopPropagation();
                    setExplainPanelData({ isOpen: true, jobData: data, type: 'commission' });
                  }}>
                  Explain
                </button>
              </If>
            </span>
          ) : (
            '-'
          ),
        width: 'w-[150px]'
      },
      {
        id: 'reimbursement',
        header: 'Team expense',
        tooltip: 'Team expenses reimbursable to this member',
        accessor: data => (data.reimbursement ? getAmountWithCurrency(data.reimbursement) : '-'),
        width: 'w-[200px]'
      },
      {
        id: 'adjustmentAmount',
        header: 'Adjustments',
        accessor: data =>
          data.adjustmentAmount ? (
            <span className="flex flex-col items-start gap-2">
              {getAmountWithCurrency(data.adjustmentAmount)}
              <If condition={data.jobAdjustmentCount > 0}>
                <button
                  className="cursor-pointer text-secondary"
                  onClick={e => {
                    e.stopPropagation();
                    setExplainPanelData({ isOpen: true, jobData: data, type: 'adjustment' });
                  }}>
                  {data.jobAdjustmentCount} {data.jobAdjustmentCount > 1 ? 'adjustments' : 'adjustment'}
                </button>
              </If>
            </span>
          ) : (
            '-'
          ),
        width: 'w-[150px]'
      },
      {
        id: 'grossPayAmount',
        header: 'Gross pay',
        tooltip: 'Balance of reimbursable expenses + Commission + Job adjustments',
        accessor: data => (data.grossPayAmount ? getAmountWithCurrency(data.grossPayAmount) : '-'),
        width: 'w-[150px]'
      },
      {
        id: 'invoiceAmount',
        header: 'Invoice total',
        accessor: data => (Number(data?.invoiceAmount) > 0 ? getAmountWithCurrency(data.invoiceAmount) : '-'),
        width: 'w-[150px]'
      },
      {
        id: 'paidAmount',
        header: 'Paid amount',
        accessor: data => (Number(data?.paidAmount) > 0 ? getAmountWithCurrency(data.paidAmount) : '-'),
        width: 'w-[150px]'
      },
      {
        id: 'commissionRate',
        header: 'Rate',
        accessor: data => (data.commissionRate ? `${formatAmount(data.commissionRate)}%` : '-'),
        width: 'w-[160px]'
      },
      {
        id: 'commissionRule',
        header: 'Rule',
        accessor: 'commissionRule',
        width: 'w-[150px]'
      },

      {
        id: 'commissionBase',
        header: 'Commission base',
        accessor: data =>
          data.commissionBase ? getCommissionBaseLabel(data.commissionBase) || data.commissionBase : '-',
        width: 'w-[170px]'
      },
      {
        id: 'commissionBaseAmount',
        header: 'Commission base amount',
        accessor: data =>
          data.commissionBaseAmount && data.commissionBase && data.commissionBase !== 'NA'
            ? getAmountWithCurrency(data.commissionBaseAmount)
            : '-',
        width: 'w-[215px]'
      },
      {
        id: 'invoiceId',
        header: 'Invoice ID',
        accessor: 'invoiceNumber',
        width: 'w-[150px]'
      },
      {
        id: 'invoiceStatus',
        header: 'Invoice status',
        accessor: job =>
          job.invoiceStatus ? (
            <div
              className="text-sbase font-bold"
              style={{ color: getInvoiceStatusColor(job.invoiceStatus as InvoiceStatus) }}>
              {getInvoiceStatus(job.invoiceStatus as InvoiceStatus)}
            </div>
          ) : (
            '-'
          ),
        width: 'w-[150px]'
      },
      {
        id: 'estimateStatus',
        header: 'Estimate status',
        accessor: job =>
          job.estimateStatus ? (
            <div
              className="text-sbase font-bold capitalize"
              style={{ color: getEstimateStatusColor(job.estimateStatus as EstimateStatus) }}>
              {job.estimateStatus?.toLowerCase()}
            </div>
          ) : (
            '-'
          ),
        width: 'w-[150px]'
      },
      {
        id: 'createdAt',
        header: 'Created On',
        accessor: job => (
          <span className="text-sbase text-primaryText">
            {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
          </span>
        ),
        width: 'w-[150px]'
      },
      {
        id: 'paymentMethod',
        header: 'Payment method',
        accessor: data => {
          return data.paymentMethod ? (
            <>
              <div className="font-semibold">{data.paymentMethod}</div>
              {data.transactionNote && (
                <div className="mt-1 text-sbase font-normal text-primaryText">{data.transactionNote}</div>
              )}
            </>
          ) : (
            '-'
          );
        },
        width: 'w-[150px]'
      },
      {
        id: 'serviceAddress',
        header: 'Service address',
        accessor: data => <div className="max-w-[250px] break-words">{data.serviceAddress}</div>,
        width: 'w-[250px]'
      },
      {
        id: 'tags',
        header: 'Tags',
        accessor: data => {
          return data.tags && data.tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {getSortedJobTags(data.tags).map(tag => (
                <TagPill
                  key={tag.id}
                  tag={tag}
                  showCloseButton={false}
                  className="rounded-md px-1.5 py-0.5"
                  textStyle="text-px13 font-semibold"
                />
              ))}
            </div>
          ) : (
            '-'
          );
        },
        width: 'w-[200px]'
      },
      {
        id: 'title',
        header: 'Job title',
        accessor: 'title',
        width: 'w-[200px]'
      },
      {
        id: 'actions',
        header: 'Actions',
        accessor: job =>
          isActiveFSMPlusPlan ? (
            <JobReviewActions
              jobId={job.jobId}
              jobNumber={job.jobNumber}
              fetchData={fetchData}
              hasDraftAdjustment={job.hasDraftAdjustment}
            />
          ) : (
            '-'
          ),
        fixed: 'right',
        width: 'w-[100px]'
      }
    ],
    [managePayAdjustments, setExplainPanelData, isActiveFSMPlusPlan, fetchData]
  );

  return (
    <>
      <div className="w-full">
        <GenericTable<PayRunMemberJob>
          data={jobs}
          columns={columns}
          columnInfo={genericColumnInfo}
          sorting={{
            sortBy: currentSortBy,
            sortOrder: currentSortOrder,
            onSortChange: (sortBy, sortOrder) => onSortChange?.(sortBy, sortOrder)
          }}
          selection={{
            getItemId: job => job.jobId,
            selectedItems: selectedJobs,
            onItemSelect: onJobCheckboxSelect,
            onSelectAll
          }}
          onRowClick={(job, e) => handleNavigateToJobReview(job.jobId, job.status, e)}
          pagination={{
            currentPage: page,
            totalCount,
            onPageChange,
            limit: 50,
            wrapperClassName: 'sticky bottom-0 z-10 w-full bg-white'
          }}
          stickyHorizontalScrollbar={stickyHorizontalScrollbar ?? { bottomOffsetPx: 80 }}
          highlightedRowId={highlightedRowId ?? undefined}
          enableRowHover={true}
          showCheckbox={true}
          className="w-full"
        />
      </div>

      {explainPanelData.jobData && explainPanelData.type === 'commission' && (
        <ExplainCommissionPanel
          isOpen={explainPanelData.isOpen}
          onClose={() => setExplainPanelData({ isOpen: false, jobData: undefined, type: 'commission' })}
          jobData={explainPanelData.jobData}
          merchantId={merchantId}
        />
      )}
      {explainPanelData.jobData && explainPanelData.type === 'adjustment' && (
        <ExplainAdjustmentsPanel
          isOpen={explainPanelData.isOpen}
          onClose={() => setExplainPanelData({ isOpen: false, jobData: undefined, type: 'adjustment' })}
          jobId={explainPanelData.jobData.jobId}
        />
      )}
    </>
  );
};

export default JobReviewTable;
